/**
 * Pet Care Professional — quartile calibration from stored biopet rows.
 *
 * Source of truth for calibration: `biopet_report_healthy` only (normal/healthy cohort).
 * The API loads that table only; `biopet_report` (unhealthy/other) is excluded.
 *
 * Pure helpers (browser-safe). The API runs the same algorithm server-side:
 *   webappbio/utils/biopetQuartileCalibrate.js
 *
 * Per species: ΔCT = species CT − universal, grouped by life stage. Q1/Q2/Q3 = percentiles
 * for that species only (not pooled across species).
 */

import { getBiopetProfessionalAgeStage } from './calculate_biopet_professional';

/** Species columns persisted on biopet_report( _healthy), same order as save-biopet-batch. */
export const BIOPET_CALIBRATION_SPECIES_COLUMNS = [
  'Bifidobacterium infantis',
  'Enterococcus faecium',
  'Lactobacillus acidophilus',
  'Lactobacillus paracasei',
  'Lactobacillus plantarum',
  'Lactobacillus rhamnosus',
  'Streptococcus thermophilus',
  'Faecalibacterium',
  'Peptacetobacter hiranomis',
  'Escherichia coli',
  'Streptococcus'
];

export const BIOPET_CALIBRATION_COLUMN_LABELS = ['Young', 'Adult', 'Senior'];

/**
 * Map age to grid column: 0 Young (&lt;1 y), 1 Adult (1–7 y), 2 Senior (&gt;7 y).
 */
export function getBiopetQuartileGridColumnIndex(ageYearsRaw) {
  const stage = getBiopetProfessionalAgeStage(ageYearsRaw);
  if (stage === 'Young') return 0;
  if (stage === 'Adult') return 1;
  return 2;
}

/**
 * @deprecated Pooled across all species — use poolBiopetDeltaCtByQuartileColumnForSpecies for calibration.
 */
export function poolBiopetDeltaCtByQuartileColumn(rows) {
  const buckets = [[], [], []];
  for (const row of rows) {
    const uni = parseFloat(String(row.universal ?? '').trim());
    if (!Number.isFinite(uni)) continue;
    const col = getBiopetQuartileGridColumnIndex(row.age);
    for (const sp of BIOPET_CALIBRATION_SPECIES_COLUMNS) {
      const raw = row[sp];
      if (raw === null || raw === undefined || raw === '') continue;
      const v = parseFloat(String(raw).trim());
      if (!Number.isFinite(v)) continue;
      buckets[col].push(v - uni);
    }
  }
  return buckets;
}

export function poolBiopetDeltaCtByQuartileColumnForSpecies(rows, speciesColumn) {
  const buckets = [[], [], []];
  for (const row of rows) {
    const uni = parseFloat(String(row.universal ?? '').trim());
    if (!Number.isFinite(uni)) continue;
    const col = getBiopetQuartileGridColumnIndex(row.age);
    const raw = row[speciesColumn];
    if (raw === null || raw === undefined || raw === '') continue;
    const v = parseFloat(String(raw).trim());
    if (!Number.isFinite(v)) continue;
    buckets[col].push(v - uni);
  }
  return buckets;
}

function quantileLinear(sorted, p) {
  const n = sorted.length;
  if (n === 0) return null;
  if (n === 1) return sorted[0];
  const pos = (n - 1) * p;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}

export function quartilesFromDeltaCtSamples(samples) {
  if (!samples || samples.length === 0) {
    return { q1: null, q2: null, q3: null };
  }
  const sorted = [...samples].sort((a, b) => a - b);
  return {
    q1: quantileLinear(sorted, 0.25),
    q2: quantileLinear(sorted, 0.5),
    q3: quantileLinear(sorted, 0.75)
  };
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

function fillGridFromBuckets(buckets, previousGrid = null) {
  const warnings = [];
  const sampleCounts = buckets.map((b) => b.length);
  const grid = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0]
  ];

  if (sampleCounts.every((c) => c === 0)) {
    return {
      values: null,
      sampleCounts,
      warnings: ['No usable ΔCT samples for this species.']
    };
  }

  const prevNum = previousGrid
    ? previousGrid.map((r) => r.map((c) => parseFloat(String(c))))
    : null;

  for (let col = 0; col < 3; col++) {
    const q = quartilesFromDeltaCtSamples(buckets[col]);
    const p1 = prevNum?.[0]?.[col];
    const p2 = prevNum?.[1]?.[col];
    const p3 = prevNum?.[2]?.[col];

    if (q.q1 !== null && q.q2 !== null && q.q3 !== null) {
      grid[0][col] = round2(q.q1);
      grid[1][col] = round2(q.q2);
      grid[2][col] = round2(q.q3);
      if (sampleCounts[col] < 5) {
        warnings.push(
          `${BIOPET_CALIBRATION_COLUMN_LABELS[col]}: only ${sampleCounts[col]} ΔCT samples — quartiles may be unstable.`
        );
      }
    } else if ([p1, p2, p3].every((x) => Number.isFinite(x))) {
      grid[0][col] = round2(p1);
      grid[1][col] = round2(p2);
      grid[2][col] = round2(p3);
      warnings.push(`${BIOPET_CALIBRATION_COLUMN_LABELS[col]}: no samples — kept previous Q1–Q3.`);
    } else {
      grid[0][col] = 0;
      grid[1][col] = 0;
      grid[2][col] = 0;
      warnings.push(`${BIOPET_CALIBRATION_COLUMN_LABELS[col]}: no samples — set to 0.`);
    }
  }

  return { values: grid.flat(), sampleCounts, warnings };
}

function flat9To313(flat) {
  if (!Array.isArray(flat) || flat.length !== 9) return null;
  if (!flat.every((x) => Number.isFinite(parseFloat(x)))) return null;
  return [
    [parseFloat(flat[0]), parseFloat(flat[1]), parseFloat(flat[2])],
    [parseFloat(flat[3]), parseFloat(flat[4]), parseFloat(flat[5])],
    [parseFloat(flat[6]), parseFloat(flat[7]), parseFloat(flat[8])]
  ];
}

/**
 * @deprecated Single pooled grid — prefer calibrateAllQuartileGridsFromBiopetRows.
 */
export function calibrateQuartileGridFromBiopetRows(rows, previousGrid = null) {
  const buckets = poolBiopetDeltaCtByQuartileColumn(rows);
  return fillGridFromBuckets(buckets, previousGrid);
}

export function calibrateQuartileGridFromBiopetRowsForSpecies(rows, speciesName, previousGrid = null) {
  const buckets = poolBiopetDeltaCtByQuartileColumnForSpecies(rows, speciesName);
  return fillGridFromBuckets(buckets, previousGrid);
}

export function meanNineVectorFromSpeciesMap(speciesMap) {
  const keys = Object.keys(speciesMap);
  if (keys.length === 0) return [0, 0, 0, 0, 0, 0, 0, 0, 0];
  const sum = new Array(9).fill(0);
  for (const sp of keys) {
    const v = speciesMap[sp];
    if (!Array.isArray(v) || v.length !== 9) continue;
    for (let i = 0; i < 9; i++) {
      const n = parseFloat(v[i]);
      sum[i] += Number.isFinite(n) ? n : 0;
    }
  }
  const nSp = keys.length;
  return sum.map((s) => round2(s / nSp));
}

/**
 * Client-side mirror of calibrateAllSpeciesFromBiopetRows (preview only).
 */
export function calibrateAllQuartileGridsFromBiopetRows(rows, previousBySpeciesFlat = null, legacy313 = null) {
  const bySpecies = {};
  const warnings = [];
  const sampleCountsBySpecies = {};

  for (const sp of BIOPET_CALIBRATION_SPECIES_COLUMNS) {
    const prevFlat = previousBySpeciesFlat?.[sp];
    const prev313 = flat9To313(prevFlat) ?? legacy313;
    const r = calibrateQuartileGridFromBiopetRowsForSpecies(rows, sp, prev313);
    sampleCountsBySpecies[sp] = r.sampleCounts;

    if (r.values) {
      bySpecies[sp] = r.values;
    } else if (prev313) {
      bySpecies[sp] = prev313.flat();
      warnings.push(`${sp}: no new samples — kept previous quartiles.`);
    } else {
      bySpecies[sp] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
      warnings.push(`${sp}: no samples — set to 0.`);
    }
    for (const w of r.warnings) warnings.push(`${sp}: ${w}`);
  }

  return {
    bySpecies,
    sampleCountsBySpecies,
    warnings,
    legacyValues: meanNineVectorFromSpeciesMap(bySpecies)
  };
}
